import { useCallback, useEffect, useRef, useState } from "react";
import { message } from "antd";
import axios from "axios";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { authenticationService } from "@/api/authentication";
import { creatorInvitationApi } from "@/api/creatorInvitation";
import { userService } from "@/api/user";
import {
	RoleRegisterDetailsStep,
	RoleRegisterLayout,
	RoleSelectorStep,
	type RegistrationRole,
	RoleRegisterVerificationStep,
	type RoleRegisterStep,
	useRoleRegisterDetailsForm,
	markSignUpSuccessPending,
	CREATOR_REGISTER_OAUTH_ERROR_KEY,
	CREATOR_REGISTER_OAUTH_NONCE_KEY,
	CREATOR_REGISTER_OAUTH_STATE_PREFIX,
	clearRoleRegisterSession,
	emptyRoleRegisterSession,
	readRoleRegisterSession,
	writeRoleRegisterSession,
	type RoleRegisterSession,
	PrivacyPolicyModal,
	BrandAgencyPrivacyNoticeModal,
} from "@/features/auth";
import { TermsOfServiceModal } from "@/shared/components";
import { CURRENT_TOS_VERSION } from "@/shared/utils/terms";
import { validatePassword } from "@/shared/utils/passwordValidation";
import { useAuth } from "@/shared/contexts/AuthContext";
import { LogAction, useUserLog } from "@/shared/lib/userLog";
import { createRandomId } from "@/shared/lib/createRandomId";
import { normalizeEmail } from "@/shared/utils/email";

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getRequestErrorMessage = (error: unknown, fallback: string): string => {
	if (axios.isAxiosError(error) && typeof error.response?.data?.message === "string") {
		return error.response.data.message;
	}
	return fallback;
};

const RoleRegister = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { login } = useAuth();
	const { logEvent } = useUserLog();
	const [searchParams] = useSearchParams();
	const inviteToken = searchParams.get("inviteToken")?.trim() || "";
	const inviteEmail = normalizeEmail(searchParams.get("inviteEmail"));
	const isInvitedRegistration = Boolean(inviteToken && inviteEmail);
	const invitationLegalAccepted =
		isInvitedRegistration && sessionStorage.getItem(`creator-invite:${inviteToken}:legal-accepted`) === "true";
	const rawStep = searchParams.get("step");
	const rawRole = searchParams.get("role");
	const registrationRole = rawRole === "creator" || rawRole === "client" || rawRole === "agency" ? rawRole : null;
	const isLegacyCreatorRoute = location.pathname === "/creator-register";
	const step: RoleRegisterStep = isInvitedRegistration
		? rawStep === "verify"
			? rawStep
			: "details"
		: registrationRole && (rawStep === "details" || rawStep === "verify")
			? rawStep
			: registrationRole
				? "details"
				: "persona";
	const [selectedPersona, setSelectedPersona] = useState<RegistrationRole | null>(registrationRole);
	const [session, setSession] = useState<RoleRegisterSession>(() => ({
		...readRoleRegisterSession(),
		...(registrationRole ? { registrationRole } : {}),
		...(isInvitedRegistration
			? { email: inviteEmail, emailVerified: false, google: undefined, accepted: invitationLegalAccepted }
			: {}),
	}));
	const [verificationCode, setVerificationCode] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [tosOpen, setTosOpen] = useState(false);
	const [privacyOpen, setPrivacyOpen] = useState(false);
	// Brands and agencies are covered by the Brand and Agency Privacy Notice; creators by the Privacy Policy.
	const isBrandOrAgencyRegistration =
		!isInvitedRegistration && (registrationRole === "client" || registrationRole === "agency");
	const [resendSeconds, setResendSeconds] = useState(0);
	const [verificationResetSignal, setVerificationResetSignal] = useState(0);
	const [verificationSuccess, setVerificationSuccess] = useState(false);
	const [verificationSubmitting, setVerificationSubmitting] = useState(false);
	const lastAutoSubmittedCodeRef = useRef<string | null>(null);
	const lastLoggedPersonaRef = useRef<RegistrationRole | null>(null);
	const {
		canSubmitDetails,
		emailError,
		updateDetails,
		clearErrors: clearDetailErrors,
		checkEmailAvailable,
	} = useRoleRegisterDetailsForm({ session, setSession, registrationRole });
	const creatorRegisterPath = useCallback(
		(nextStep?: RoleRegisterStep) => {
			const params = new URLSearchParams();
			if (registrationRole && nextStep !== "persona") params.set("role", registrationRole);
			if (nextStep && nextStep !== "persona") params.set("step", nextStep);
			if (inviteToken) params.set("inviteToken", inviteToken);
			if (inviteEmail) params.set("inviteEmail", inviteEmail);
			const query = params.toString();
			return `/register${query ? `?${query}` : ""}`;
		},
		[inviteEmail, inviteToken, registrationRole],
	);

	useEffect(() => {
		if (!isLegacyCreatorRoute) return;
		const params = new URLSearchParams(searchParams);
		params.set("role", "creator");
		navigate(`/register?${params.toString()}`, { replace: true });
	}, [isLegacyCreatorRoute, navigate, searchParams]);

	useEffect(() => {
		if (registrationRole) setSession((current) => ({ ...current, registrationRole }));
	}, [registrationRole]);

	useEffect(() => {
		if (!isInvitedRegistration || session.email === inviteEmail) return;
		setSession((current) => ({
			...current,
			email: inviteEmail,
			emailVerified: false,
			google: undefined,
			accepted: invitationLegalAccepted,
		}));
	}, [invitationLegalAccepted, inviteEmail, isInvitedRegistration, session.email]);

	useEffect(() => {
		writeRoleRegisterSession(session);
	}, [session]);

	useEffect(() => {
		if (step === "verify" && !emailPattern.test(session.email)) {
			navigate(creatorRegisterPath("details"), { replace: true });
		}
	}, [creatorRegisterPath, navigate, session.email, session.emailVerified, step]);

	useEffect(() => {
		const oauthError = sessionStorage.getItem(CREATOR_REGISTER_OAUTH_ERROR_KEY);
		if (oauthError) {
			setError(oauthError);
			sessionStorage.removeItem(CREATOR_REGISTER_OAUTH_ERROR_KEY);
		}
	}, []);

	useEffect(() => {
		if (resendSeconds <= 0) return;
		const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
		return () => window.clearInterval(timer);
	}, [resendSeconds]);

	useEffect(() => {
		if (session.google && session.google.expiresAt <= Date.now()) {
			setSession((current) => ({ ...current, google: undefined, email: "", emailVerified: false }));
			setError("Your Google signup session expired. Please connect Google again.");
		}
	}, [session.google]);

	const passwordValid = validatePassword(password).isValid;
	const passwordError =
		!session.google && confirmPassword.length > 0 && password !== confirmPassword ? "Passwords do not match." : "";
	const agencyEmailError =
		registrationRole === "agency" && session.email && !normalizeEmail(session.email).endsWith("@thisisamplify.com")
			? "Email domain not recognized (registered agency users only: @amplify.com)"
			: "";
	const canCreateAccount =
		canSubmitDetails &&
		!agencyEmailError &&
		(Boolean(session.google) || (passwordValid && password === confirmPassword));
	const submitDetails = async () => {
		if (!canCreateAccount) return;
		setLoading(true);
		setError("");
		try {
			if (session.google && !isInvitedRegistration) {
				const result = await authenticationService.completeGoogleSignup({
					signupToken: session.google.signupToken,
					firstName: session.firstName.trim() || undefined,
					lastName: session.lastName.trim() || undefined,
					tosAccepted: true,
					tosVersion: CURRENT_TOS_VERSION,
				});
				login(result.user);
				markSignUpSuccessPending(result.user.email, "google");
				clearRoleRegisterSession();
				message.success("Registration successful!");
				navigate("/campaigns", { replace: true });
				return;
			}

			if (!(await checkEmailAvailable())) return;
			const email = normalizeEmail(session.email);
			await userService.resendVerificationEmail(email);
			updateDetails({ email, emailVerified: false });
			setResendSeconds(60);
			navigate(creatorRegisterPath("verify"));
		} catch (requestError: unknown) {
			setError(getRequestErrorMessage(requestError, "We couldn't continue your signup. Please try again."));
		} finally {
			setLoading(false);
		}
	};

	const createPasswordAccount = useCallback(
		async (emailJustVerified = false) => {
			if (!passwordValid) {
				setError("Your password details are no longer available. Please go back and enter them again.");
				return;
			}
			if (!session.emailVerified && !emailJustVerified) {
				setError("Verify your email before creating your account.");
				return;
			}
			setLoading(true);
			setError("");
			try {
				const name =
					registrationRole === "creator" || isInvitedRegistration
						? `${session.firstName.trim()} ${session.lastName.trim()}`.trim()
						: normalizeEmail(session.email);
				const registration = {
					email: normalizeEmail(session.email),
					password,
					name,
					tosAccepted: true,
					tosVersion: CURRENT_TOS_VERSION,
					documentTypes: ["terms_of_service", "privacy_policy"] as Array<
						"terms_of_service" | "privacy_policy"
					>,
				};
				if (isInvitedRegistration) {
					const result = await creatorInvitationApi.register(inviteToken, {
						...registration,
						documentTypes: ["terms_of_service", "fee_schedule", "privacy_policy"],
					});
					markSignUpSuccessPending(result.user.email);
					login(result.user);
					sessionStorage.removeItem(`creator-invite:${inviteToken}:legal-accepted`);
					clearRoleRegisterSession();
					message.success("Registration successful!");
					navigate("/campaigns", { replace: true });
					return;
				}

				if (registrationRole === "creator") await userService.registerCreator(registration);
				else await userService.register(registration);
				clearRoleRegisterSession();
				const loginData = await authenticationService.login({
					email: registration.email,
					password,
				});
				login(loginData.user);
				markSignUpSuccessPending(loginData.user.email);
				message.success("Registration successful!");
				navigate("/campaigns", { replace: true });
			} catch (requestError: unknown) {
				setError(getRequestErrorMessage(requestError, "Failed to create your account. Please try again."));
			} finally {
				setLoading(false);
			}
		},
		[
			inviteToken,
			isInvitedRegistration,
			login,
			navigate,
			password,
			passwordValid,
			registrationRole,
			session.email,
			session.emailVerified,
			session.firstName,
			session.lastName,
		],
	);

	const verifyEmail = useCallback(
		async (code: string) => {
			if (code.length !== 6) return;
			setLoading(true);
			setVerificationSubmitting(true);
			setError("");
			try {
				await userService.verifyEmail(session.email, code);
				setVerificationSuccess(true);
				setSession((current) => ({ ...current, emailVerified: true }));
				setError("");
				await createPasswordAccount(true);
				setVerificationSuccess(false);
			} catch {
				setError("Invalid code");
				setVerificationCode("");
				setVerificationResetSignal((value) => value + 1);
				lastAutoSubmittedCodeRef.current = null;
				setVerificationSuccess(false);
			} finally {
				setVerificationSubmitting(false);
				setLoading(false);
			}
		},
		[createPasswordAccount, session.email],
	);

	const handleVerificationCodeChange = (code: string) => {
		const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
		setVerificationCode(normalizedCode);
		if (normalizedCode.length < 6) {
			lastAutoSubmittedCodeRef.current = null;
			// The shared input reports an empty value when an invalid submission is
			// reset. Keep the API error visible until the user starts typing again.
			if (normalizedCode.length > 0) setError("");
		}
	};

	const handleVerificationCodeComplete = (code: string) => {
		const normalizedCode = code.replace(/\D/g, "").slice(0, 6);
		setVerificationCode(normalizedCode);
	};

	useEffect(() => {
		if (step !== "verify" || loading || verificationSuccess) return;

		const normalizedCode = verificationCode.replace(/\D/g, "").slice(0, 6);
		if (normalizedCode.length === 6 && lastAutoSubmittedCodeRef.current !== normalizedCode) {
			lastAutoSubmittedCodeRef.current = normalizedCode;
			void verifyEmail(normalizedCode);
			return;
		}

		if (normalizedCode.length < 6) {
			lastAutoSubmittedCodeRef.current = null;
		}
	}, [loading, step, verificationCode, verificationSuccess, verifyEmail]);

	const resendCode = async () => {
		if (resendSeconds > 0) return;
		setLoading(true);
		try {
			await userService.resendVerificationEmail(session.email);
			setVerificationCode("");
			setVerificationResetSignal((value) => value + 1);
			lastAutoSubmittedCodeRef.current = null;
			setResendSeconds(60);
			setError("");
			message.success("A new verification code was sent.");
		} catch (requestError: unknown) {
			setError(getRequestErrorMessage(requestError, "Failed to resend the code."));
		} finally {
			setLoading(false);
		}
	};

	const startGoogleSignup = () => {
		const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
		if (!clientId) {
			setError("Google signup is not configured.");
			return;
		}
		const nonce = createRandomId();
		sessionStorage.setItem(CREATOR_REGISTER_OAUTH_NONCE_KEY, nonce);
		const redirectUri = `${window.location.origin}/auth/callback`;
		const url = new URL("https://accounts.google.com/o/oauth2/v2/auth");
		url.searchParams.set("client_id", clientId);
		url.searchParams.set("redirect_uri", redirectUri);
		url.searchParams.set("response_type", "code");
		url.searchParams.set("scope", "openid email profile");
		url.searchParams.set("prompt", "select_account");
		url.searchParams.set("state", `${CREATOR_REGISTER_OAUTH_STATE_PREFIX}${nonce}`);
		window.location.assign(url.toString());
	};

	const changeGoogleAccount = () => {
		setSession({ ...emptyRoleRegisterSession(), firstName: session.firstName, lastName: session.lastName });
		clearDetailErrors();
		setError("");
		navigate(creatorRegisterPath(), { replace: true });
	};

	const goBack = () => {
		setLoading(false);
		setVerificationSubmitting(false);
		setVerificationSuccess(false);
		setError("");
		if (step === "verify") navigate(creatorRegisterPath("details"));
		else if (step === "details") {
			if (session.google) {
				setSession(emptyRoleRegisterSession());
				setPassword("");
				setConfirmPassword("");
				clearDetailErrors();
				clearRoleRegisterSession();
			}
			if (isInvitedRegistration) navigate(`/creator-invitations/${inviteToken}`);
			else navigate(creatorRegisterPath("persona"));
		} else navigate("/");
	};

	const continueFromPersona = () => {
		if (!selectedPersona) return;
		navigate(`/register?role=${selectedPersona}`);
	};

	const selectPersona = (persona: RegistrationRole) => {
		setSelectedPersona(persona);
		if (lastLoggedPersonaRef.current === persona) return;
		lastLoggedPersonaRef.current = persona;
		logEvent({
			action: LogAction.SIGNUP_PERSONA_SELECTED,
			properties: { persona: persona === "client" ? "brand" : persona },
		});
	};

	return (
		<>
			<RoleRegisterLayout step={step} email={session.email} error={error} emailError={emailError} onBack={goBack}>
				{step === "persona" && (
					<RoleSelectorStep
						selected={selectedPersona}
						onSelect={selectPersona}
						onContinue={continueFromPersona}
					/>
				)}

				{step === "details" && (
					<RoleRegisterDetailsStep
						session={session}
						emailError={emailError}
						role={registrationRole || "creator"}
						agencyEmailError={agencyEmailError}
						canSubmit={canCreateAccount}
						loading={loading}
						onSessionChange={updateDetails}
						onCheckEmail={() => void checkEmailAvailable()}
						onOpenTerms={() => setTosOpen(true)}
						onOpenPrivacy={() => setPrivacyOpen(true)}
						onSubmit={() => void submitDetails()}
						onGoogleSignup={startGoogleSignup}
						onChangeGoogleAccount={changeGoogleAccount}
						emailLocked={isInvitedRegistration}
						allowGoogleSignup={!isInvitedRegistration}
						password={password}
						confirmPassword={confirmPassword}
						passwordError={passwordError}
						onPasswordChange={setPassword}
						onConfirmPasswordChange={setConfirmPassword}
					/>
				)}

				{step === "verify" && (
					<RoleRegisterVerificationStep
						error={error}
						loading={loading}
						resetSignal={verificationResetSignal}
						isSuccess={verificationSuccess}
						isSubmitting={verificationSubmitting}
						resendSeconds={resendSeconds}
						onCodeChange={handleVerificationCodeChange}
						onCodeComplete={handleVerificationCodeComplete}
						onResend={() => void resendCode()}
					/>
				)}
			</RoleRegisterLayout>
			<TermsOfServiceModal open={tosOpen} onClose={() => setTosOpen(false)} />
			{isBrandOrAgencyRegistration ? (
				<BrandAgencyPrivacyNoticeModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
			) : (
				<PrivacyPolicyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
			)}
		</>
	);
};

export default RoleRegister;
